// VoltPack — formulaires de devis (impression 3D, CAO) et de contact.
// Amélioration progressive du formulaire de contact Shopify : sans JavaScript,
// le formulaire part sans fichiers. Avec JavaScript : contrôles accessibles,
// glisser-déposer, aperçu 3D local, envoi direct des fichiers vers le stockage
// privé VoltPack (autorisations signées par la fonction « signer-envoi »), puis
// envoi du formulaire natif avec la référence et la liste des fichiers.
// Mode local (fonction non configurée) : les fichiers restent sur l'appareil,
// seuls leurs noms et tailles accompagnent la demande.
// Les contrôles faits ici sont un confort : la fonction refait les siens.

export const MO = 1024 * 1024;
// Même liste que fonction/signer-envoi/lib/regles.js (vérifié par les tests).
export const SEGMENTS_DANGEREUX = [
  'exe', 'bat', 'cmd', 'com', 'scr', 'msi', 'dll', 'js', 'mjs', 'cjs', 'vbs',
  'vbe', 'ps1', 'sh', 'jar', 'app', 'html', 'htm', 'xhtml', 'svg', 'php',
  'hta', 'lnk', 'reg', 'wsf', 'pif', 'dmg', 'pkg', 'iso',
];
const CLE_REFERENCE = 'vp-devis-reference';

export function extensionDe(nom) {
  const i = nom.lastIndexOf('.');
  return i < 0 ? '' : nom.slice(i + 1).toLowerCase();
}

export function formaterTaille(octets) {
  const nf = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });
  if (octets >= MO) return `${nf.format(octets / MO)} Mo`;
  return `${nf.format(Math.max(1, Math.round(octets / 1024)))} ko`;
}

// fichiers : [{ name, size }] ; regles : { formats, maxFichiers, maxOctetsFichier, maxOctetsTotal }
export function validerSelection(fichiers, regles) {
  const erreurs = [];
  let total = 0;
  if (fichiers.length > regles.maxFichiers) {
    erreurs.push({ index: null, message: `Vous pouvez joindre ${regles.maxFichiers} fichiers au maximum.` });
  }
  fichiers.forEach((f, index) => {
    total += f.size;
    const interieurs = f.name.toLowerCase().split('.').slice(1, -1);
    if (!regles.formats.includes(extensionDe(f.name))) {
      erreurs.push({ index, message: `« ${f.name} » : format non accepté (formats acceptés : ${regles.formats.join(', ')}).` });
    } else if (interieurs.some((segment) => SEGMENTS_DANGEREUX.includes(segment))) {
      erreurs.push({ index, message: `« ${f.name} » : nom de fichier refusé.` });
    } else if (f.size < 1) {
      erreurs.push({ index, message: `« ${f.name} » est vide.` });
    } else if (f.size > regles.maxOctetsFichier) {
      erreurs.push({ index, message: `« ${f.name} » dépasse ${formaterTaille(regles.maxOctetsFichier)}.` });
    }
  });
  if (total > regles.maxOctetsTotal) {
    erreurs.push({ index: null, message: `L'ensemble des fichiers dépasse ${formaterTaille(regles.maxOctetsTotal)}.` });
  }
  return { erreurs, total };
}

export function texteFichiers(envoyes) {
  return envoyes
    .map((f, i) => `${String(i + 1).padStart(2, '0')} · ${f.nom} · ${formaterTaille(f.taille)} · ${f.cle}`)
    .join('\n');
}

// Mode local : seuls les noms et tailles accompagnent la demande (les fichiers restent chez le client).
export function texteFichiersLocaux(fichiers) {
  return ['Fichiers choisis, non envoyés (dépôt en ligne pas encore ouvert) : à demander au client.',
    ...fichiers.map((f, i) => `${String(i + 1).padStart(2, '0')} · ${f.name} · ${formaterTaille(f.size)}`)].join('\n');
}

function lireStockage(cle) {
  try { return window.sessionStorage.getItem(cle); } catch { return null; }
}
function ecrireStockage(cle, valeur) {
  try {
    if (valeur === null) window.sessionStorage.removeItem(cle);
    else window.sessionStorage.setItem(cle, valeur);
  } catch { /* stockage indisponible : la référence ne sera pas réaffichée */ }
}

function envoyerFichier(autorisation, fichier, surProgression) {
  return new Promise((resoudre, rejeter) => {
    const donnees = new FormData();
    Object.entries(autorisation.champs).forEach(([cle, valeur]) => donnees.append(cle, valeur));
    donnees.append('file', fichier);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', autorisation.url);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) surProgression(e.loaded / e.total);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resoudre();
      else rejeter(new Error(`stockage ${xhr.status}`));
    });
    xhr.addEventListener('error', () => rejeter(new Error('réseau')));
    xhr.addEventListener('abort', () => rejeter(new Error('interrompu')));
    xhr.send(donnees);
  });
}

class FormulaireDevis {
  constructor(racine) {
    this.racine = racine;
    this.form = racine.querySelector('form');
    const d = racine.dataset;
    this.type = d.type;
    this.urlFonction = (d.urlFonction || '').trim();
    this.urlVisionneuse = d.urlVisionneuse;
    this.seuilApercu = Number(d.seuilApercuMo || 60) * MO;
    this.regles = {
      formats: (d.formats || '').split(',').map((s) => s.trim()).filter(Boolean),
      maxFichiers: Number(d.maxFichiers || 10),
      maxOctetsFichier: Number(d.maxMo || 100) * MO,
      maxOctetsTotal: Number(d.maxMoTotal || 1000) * MO,
    };
    this.selection = [];
    this.apercu = null;
    this.envoiEnCours = false;

    this.champFichiers = racine.querySelector('[data-vp-champ-fichiers]');
    this.blocFichiers = racine.querySelector('[data-vp-bloc-fichiers]');
    this.liste = racine.querySelector('[data-vp-liste]');
    this.zoneApercu = racine.querySelector('[data-vp-apercu]');
    this.resume = racine.querySelector('[data-vp-resume]');
    this.etat = racine.querySelector('[data-vp-etat]');
    this.boutonEnvoyer = racine.querySelector('[data-vp-envoyer]');
    this.boutonSansFichiers = racine.querySelector('[data-vp-sans-fichiers]');
    this.champReference = racine.querySelector('[data-vp-reference-champ]');
    this.champListe = racine.querySelector('[data-vp-fichiers-champ]');
    this.caseDroits = racine.querySelector('[data-vp-droits]');
    this.blocDroits = racine.querySelector('[data-vp-bloc-droits]');
    this.depot = racine.querySelector('[data-vp-depot]');
    this.modeLocal = !this.urlFonction;
  }

  initialiser() {
    this.afficherReferencePrecedente();
    this.form.noValidate = true;
    this.form.addEventListener('submit', (e) => this.surEnvoi(e));
    this.initialiserTypeClient();
    this.initialiserCouleurs();

    if (!this.blocFichiers || !this.champFichiers) return;
    this.blocFichiers.hidden = false;
    const noteLocale = this.racine.querySelector('[data-vp-mode-local]');
    if (noteLocale) noteLocale.hidden = !this.modeLocal;
    const infoStockage = this.racine.querySelector('[data-vp-info-stockage]');
    if (infoStockage) infoStockage.hidden = this.modeLocal;
    this.champFichiers.addEventListener('change', () => {
      this.ajouter(Array.from(this.champFichiers.files || []));
      this.champFichiers.value = '';
    });
    this.initialiserDepot();
    if (this.boutonSansFichiers) {
      this.boutonSansFichiers.addEventListener('click', () => this.envoyerSansFichiers());
    }
  }

  initialiserDepot() {
    if (!this.depot) return;
    const actif = (oui) => this.depot.classList.toggle('vp-devis__depot--actif', oui);
    ['dragenter', 'dragover'].forEach((type) => this.depot.addEventListener(type, (e) => { e.preventDefault(); actif(true); }));
    this.depot.addEventListener('dragleave', (e) => { if (!this.depot.contains(e.relatedTarget)) actif(false); });
    this.depot.addEventListener('drop', (e) => {
      e.preventDefault();
      actif(false);
      this.ajouter(Array.from((e.dataTransfer && e.dataTransfer.files) || []));
    });
  }

  afficherReferencePrecedente() {
    const cible = this.racine.querySelector('[data-vp-reference-succes]');
    const reference = lireStockage(CLE_REFERENCE);
    if (cible && reference) {
      cible.textContent = ` Votre référence : ${reference}.`;
      ecrireStockage(CLE_REFERENCE, null);
    }
  }

  initialiserTypeClient() {
    const radios = this.form.querySelectorAll('[data-vp-type-client]');
    const blocPro = this.racine.querySelector('[data-vp-si-pro]');
    if (!radios.length || !blocPro) return;
    const societe = blocPro.querySelector('[data-vp-societe]');
    const maj = () => {
      const pro = Array.from(radios).some((r) => r.checked && r.value === 'Professionnel');
      blocPro.hidden = !pro;
      if (societe) societe.required = pro;
    };
    radios.forEach((r) => r.addEventListener('change', maj));
    maj();
  }

  initialiserCouleurs() {
    const materiau = this.form.querySelector('[data-vp-materiau]');
    const couleur = this.form.querySelector('[data-vp-couleur]');
    if (!materiau || !couleur) return;
    const maj = () => {
      const choisi = materiau.value;
      couleur.querySelectorAll('optgroup').forEach((groupe) => {
        const visible = groupe.dataset.materiau === choisi;
        groupe.hidden = !visible;
        groupe.disabled = !visible;
      });
      const option = couleur.selectedOptions[0];
      if (option && option.parentElement.tagName === 'OPTGROUP' && option.parentElement.disabled) {
        couleur.value = '';
      }
    };
    materiau.addEventListener('change', maj);
    maj();
  }

  ajouter(nouveaux) {
    nouveaux.forEach((fichier) => {
      const doublon = this.selection.some((s) => s.fichier.name === fichier.name
        && s.fichier.size === fichier.size && s.fichier.lastModified === fichier.lastModified);
      if (!doublon) this.selection.push({ fichier, statut: 'choisi' });
    });
    this.rendreListe();
    this.annoncer(`${this.selection.length} fichier(s) sélectionné(s).`);
    // Aperçu automatique du premier fichier affichable, si aucun aperçu n'est ouvert.
    if (!this.apercu && this.zoneApercu.hidden) {
      const { erreurs } = validerSelection(this.selection.map((s) => s.fichier), this.regles);
      const index = this.selection.findIndex((s, i) => this.apercuPossible(s.fichier) && !erreurs.some((e) => e.index === i));
      if (index >= 0) this.ouvrirApercu(this.selection[index].fichier);
    }
  }

  apercuPossible(fichier) {
    return ['stl', 'obj', '3mf'].includes(extensionDe(fichier.name)) && fichier.size <= this.seuilApercu;
  }

  retirer(index) {
    const [retire] = this.selection.splice(index, 1);
    if (this.apercu && this.apercu.fichier === retire.fichier) this.fermerApercu();
    this.rendreListe();
    this.annoncer(`« ${retire.fichier.name} » retiré.`);
    this.champFichiers.focus();
  }

  rendreListe() {
    const { erreurs } = validerSelection(this.selection.map((s) => s.fichier), this.regles);
    this.liste.replaceChildren(...this.selection.map((s, index) => {
      const li = document.createElement('li');
      li.className = 'vp-devis__fichier';
      const erreur = erreurs.find((e) => e.index === index);
      const nom = document.createElement('span');
      nom.className = 'vp-devis__fichier-nom';
      nom.textContent = `${s.fichier.name} (${formaterTaille(s.fichier.size)})`;
      const statut = document.createElement('span');
      statut.className = 'vp-devis__fichier-statut';
      statut.dataset.vpStatut = '';
      statut.textContent = erreur ? erreur.message : this.libelleStatut(s.statut);
      if (erreur) li.classList.add('vp-devis__fichier--erreur');
      li.append(nom, statut);

      const ext = extensionDe(s.fichier.name);
      if (this.apercuPossible(s.fichier) && !erreur) {
        li.append(this.bouton(`Aperçu 3D`, `Afficher l'aperçu 3D de ${s.fichier.name}`, () => this.ouvrirApercu(s.fichier)));
      } else if (['step', 'stp'].includes(ext) && !erreur) {
        const note = document.createElement('span');
        note.className = 'vp-devis__fichier-note';
        note.textContent = 'Aperçu fourni avec le devis.';
        li.append(note);
      }
      li.append(this.bouton('Retirer', `Retirer ${s.fichier.name}`, () => this.retirer(index)));
      return li;
    }));
    const generales = erreurs.filter((e) => e.index === null);
    const zoneGenerale = this.racine.querySelector('[data-vp-erreur-fichiers]');
    if (zoneGenerale) {
      zoneGenerale.textContent = generales.map((e) => e.message).join(' ');
      zoneGenerale.hidden = generales.length === 0;
    }
    // La confirmation des droits n'est demandée que si des fichiers sont réellement envoyés.
    const envoi = !this.modeLocal && this.selection.length > 0;
    if (this.caseDroits) this.caseDroits.required = envoi;
    if (this.blocDroits) this.blocDroits.hidden = !envoi;
  }

  libelleStatut(statut) {
    return {
      choisi: this.modeLocal ? 'Sur votre appareil (non envoyé)' : 'Prêt à envoyer', envoi: 'Envoi en cours…', envoye: 'Envoyé', echec: 'Échec de l\'envoi',
    }[statut] || '';
  }

  bouton(texte, libelle, action) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'button-secondary vp-devis__bouton-fichier';
    b.textContent = texte;
    b.setAttribute('aria-label', libelle);
    b.addEventListener('click', action);
    return b;
  }

  async ouvrirApercu(fichier) {
    this.fermerApercu();
    this.zoneApercu.hidden = false;
    this.zoneApercu.textContent = 'Chargement de l\'aperçu…';
    try {
      const { creerApercu } = await import(this.urlVisionneuse);
      const instance = await creerApercu(this.zoneApercu, fichier);
      this.apercu = { fichier, instance };
      this.annoncer(`Aperçu de « ${fichier.name} » affiché.`);
    } catch (e) {
      const message = (e && e.code && e.message) ? e.message : 'Aperçu indisponible.';
      this.zoneApercu.textContent = `${message} Fichier : ${fichier.name}, ${formaterTaille(fichier.size)}. L'envoi reste possible.`;
      this.annoncer(this.zoneApercu.textContent);
    }
  }

  fermerApercu() {
    if (this.apercu) this.apercu.instance.detruire();
    this.apercu = null;
    this.zoneApercu.hidden = true;
    this.zoneApercu.replaceChildren();
  }

  annoncer(texte) {
    if (this.etat) this.etat.textContent = texte;
  }

  controlerChamps() {
    const erreurs = [];
    this.form.querySelectorAll('input, select, textarea').forEach((champ) => {
      if (champ.disabled || champ.type === 'hidden' || champ.closest('[hidden]')) return;
      const valide = champ.checkValidity();
      champ.setAttribute('aria-invalid', valide ? 'false' : 'true');
      if (!valide && !erreurs.some((e) => e.champ.name === champ.name && champ.type === 'radio')) {
        const libelle = (champ.id && this.form.querySelector(`label[for="${champ.id}"]`))
          || (champ.type !== 'radio' && champ.closest('label'))
          || champ.closest('fieldset')?.querySelector('legend');
        const nom = libelle ? libelle.textContent.replace('*', '').trim() : champ.name;
        erreurs.push({ champ, message: `${nom} : ${champ.validationMessage}` });
      }
    });
    const { erreurs: erreursFichiers } = validerSelection(this.selection.map((s) => s.fichier), this.regles);
    erreursFichiers.forEach((e) => erreurs.push({ champ: this.champFichiers, message: e.message }));
    return erreurs;
  }

  afficherResume(erreurs) {
    if (!erreurs.length) {
      this.resume.hidden = true;
      this.resume.replaceChildren();
      return;
    }
    const titre = document.createElement('p');
    titre.textContent = erreurs.length === 1
      ? 'Une information est à corriger :'
      : `${erreurs.length} informations sont à corriger :`;
    const liste = document.createElement('ul');
    erreurs.forEach(({ champ, message }) => {
      const li = document.createElement('li');
      const lien = document.createElement('a');
      lien.href = `#${champ.id}`;
      lien.textContent = message;
      lien.addEventListener('click', (e) => { e.preventDefault(); this.ouvrirParents(champ); champ.focus(); });
      li.append(lien);
      liste.append(li);
    });
    erreurs.forEach(({ champ }) => this.ouvrirParents(champ));
    this.resume.replaceChildren(titre, liste);
    this.resume.hidden = false;
    this.resume.focus();
  }

  ouvrirParents(champ) {
    const details = champ && champ.closest('details');
    if (details) details.open = true;
  }

  soumettreNatif() {
    HTMLFormElement.prototype.submit.call(this.form);
  }

  async surEnvoi(evenement) {
    evenement.preventDefault();
    if (this.envoiEnCours) return;
    const erreurs = this.controlerChamps();
    this.afficherResume(erreurs);
    if (erreurs.length) return;
    if (this.selection.length && this.modeLocal) {
      this.champListe.value = texteFichiersLocaux(this.selection.map((s) => s.fichier));
      this.soumettreNatif();
      return;
    }
    if (!this.selection.length) {
      this.soumettreNatif();
      return;
    }
    await this.envoyerAvecFichiers();
  }

  async envoyerAvecFichiers() {
    this.envoiEnCours = true;
    this.form.setAttribute('aria-busy', 'true');
    this.boutonEnvoyer.disabled = true;
    if (this.boutonSansFichiers) this.boutonSansFichiers.hidden = true;
    this.annoncer('Préparation de l\'envoi des fichiers…');

    let reponse;
    try {
      const r = await fetch(this.urlFonction, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: this.type,
          fichiers: this.selection.map((s) => ({ nom: s.fichier.name, taille: s.fichier.size })),
        }),
      });
      reponse = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(reponse.message || 'Le service d\'envoi a refusé la demande.');
    } catch (e) {
      this.echec(e.message && !e.message.startsWith('Failed') ? e.message : 'Le service d\'envoi est momentanément indisponible.');
      return;
    }

    const envoyes = [];
    let echecs = 0;
    for (const autorisation of reponse.fichiers) {
      const element = this.selection[autorisation.index];
      element.statut = 'envoi';
      this.rendreListe();
      let palier = 0;
      try {
        await envoyerFichier(autorisation, element.fichier, (part) => {
          const pourcent = Math.floor(part * 4) * 25;
          if (pourcent > palier) {
            palier = pourcent;
            this.annoncer(`« ${element.fichier.name} » : ${pourcent} %.`);
          }
        });
        element.statut = 'envoye';
        envoyes.push({ nom: autorisation.nom, taille: autorisation.taille, cle: autorisation.cle });
      } catch {
        element.statut = 'echec';
        echecs += 1;
      }
      this.rendreListe();
    }

    if (echecs) {
      this.echec(`${echecs} fichier(s) n'ont pas pu être envoyés. Vous pouvez réessayer, ou envoyer la demande sans les fichiers : nous vous enverrons un lien.`);
      return;
    }
    this.champReference.value = reponse.reference;
    this.champListe.value = texteFichiers(envoyes);
    ecrireStockage(CLE_REFERENCE, reponse.reference);
    this.annoncer('Fichiers envoyés. Envoi de la demande…');
    this.soumettreNatif();
  }

  echec(message) {
    this.envoiEnCours = false;
    this.form.removeAttribute('aria-busy');
    this.boutonEnvoyer.disabled = false;
    this.selection.forEach((s) => { if (s.statut !== 'envoye') s.statut = 'choisi'; });
    this.rendreListe();
    this.annoncer(message);
    this.afficherResume([{ champ: this.champFichiers, message }]);
    if (this.boutonSansFichiers) this.boutonSansFichiers.hidden = false;
  }

  envoyerSansFichiers() {
    this.champReference.value = '';
    this.champListe.value = `Aucun fichier joint (${this.selection.length} fichier(s) non envoyé(s)) : envoyer un lien de dépôt au client.`;
    if (this.caseDroits) this.caseDroits.required = false;
    this.soumettreNatif();
  }
}

function demarrer() {
  document.querySelectorAll('[data-vp-devis]').forEach((racine) => {
    if (racine.dataset.vpPret) return;
    racine.dataset.vpPret = 'oui';
    new FormulaireDevis(racine).initialiser();
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();
  document.addEventListener('shopify:section:load', demarrer);
}
